import axios from 'axios';
import { baseURL } from '../Constants';
import { Account } from '../models/Account';

export interface SearchResultTeam {
  id: string;
  name: string;
  logo: string | null;
  currentSeason: number | null;
  currentDivision: string | null;
  currentSeed: string | null;
}

export interface SearchResultAccount extends Account {
  team?: SearchResultTeam;
  teamSeason?: number;
}

export interface SearchResults {
  players: SearchResultAccount[];
  teams: SearchResultTeam[];
}

export interface RecentSearchItem {
  type: 'player' | 'team';
  id: string;
  name: string;
  season?: number | null;
  teamName?: string; // For players, to show their team
}

const RECENT_SEARCHES_KEY = 'ncs-recent-searches';
const MAX_RECENT_SEARCHES = 4;

class SearchService {
  // Search players by name (uses existing backend endpoint)
  async searchPlayers(query: string): Promise<SearchResultAccount[]> {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const response = await axios.get(`${baseURL}/Account/search/${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching players:', error);
      return [];
    }
  }

  // Search teams by name (uses new backend endpoint)
  async searchTeams(query: string): Promise<SearchResultTeam[]> {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const url = `${baseURL}/Team/search/${encodeURIComponent(query)}`;
      console.log('Searching teams with URL:', url);

      const response = await axios.get(url);
      console.log('Team search response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error searching teams:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
      }
      return [];
    }
  }

  // Search both players and teams simultaneously
  async searchAll(query: string): Promise<SearchResults> {
    try {
      if (!query || query.length < 2) {
        return { players: [], teams: [] };
      }

      // Execute both searches in parallel for better performance
      const [players, teams] = await Promise.all([
        this.searchPlayers(query),
        this.searchTeams(query)
      ]);

      return { players, teams };
    } catch (error) {
      console.error('Error performing combined search:', error);
      return { players: [], teams: [] };
    }
  }

  // Recent searches management
  getRecentSearches(): RecentSearchItem[] {
    try {
      const recent = localStorage.getItem(RECENT_SEARCHES_KEY);
      return recent ? JSON.parse(recent) : [];
    } catch (error) {
      console.error('Error retrieving recent searches:', error);
      return [];
    }
  }

  addRecentSearch(item: RecentSearchItem): void {
    try {
      if (!item || !item.name || !item.id) {
        return;
      }

      let recentSearches = this.getRecentSearches();

      // Remove duplicate if exists (same type and id)
      recentSearches = recentSearches.filter(s =>
        !(s.type === item.type && s.id === item.id)
      );

      // Add to beginning of array
      recentSearches.unshift(item);

      // Limit to MAX_RECENT_SEARCHES
      if (recentSearches.length > MAX_RECENT_SEARCHES) {
        recentSearches = recentSearches.slice(0, MAX_RECENT_SEARCHES);
      }

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches));
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  }

  clearRecentSearches(): void {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
      console.error('Error clearing recent searches:', error);
    }
  }
}

export const searchService = new SearchService();
